// /campus/cases — Coravu cases where the school's students participated.
// Click a row to expand inline and see which of the school's students
// joined that case, whether they submitted, and any placements.

import type { Href } from "expo-router";
import { COLORS } from "../../../lib/brand";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import {
  listCompetitionStudents,
  listMyCompetitions,
} from "../../../lib/campus";
import type {
  CampusCompetitionRow,
  CampusCompetitionStudentRow,
} from "../../../types/database";

const BLACK = COLORS.BLACK.hex;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.SAGE.hex;
const LINEN = COLORS.LINEN.hex;
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
void BLACK;

type StatusFilter = "active" | "closed" | "all";

export default function CampusCasesScreen() {
  const params = useLocalSearchParams<{ case?: string | string[] }>();
  const initialCaseId = useMemo(() => {
    const v = params.case;
    return typeof v === "string" ? v : Array.isArray(v) ? v[0] ?? null : null;
  }, [params.case]);

  const [rows, setRows] = useState<CampusCompetitionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [openCaseId, setOpenCaseId] = useState<string | null>(initialCaseId);
  const [rosterById, setRosterById] = useState<Map<string, CampusCompetitionStudentRow[]>>(new Map());
  const [rosterLoadingId, setRosterLoadingId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      setRows(await listMyCompetitions());
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  // Auto-expand the case from the query param once the rows have loaded.
  useEffect(() => {
    if (initialCaseId && rows.length > 0 && !rosterById.has(initialCaseId)) {
      void loadRoster(initialCaseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCaseId, rows]);

  async function loadRoster(competitionId: string) {
    if (rosterById.has(competitionId)) return;
    setRosterLoadingId(competitionId);
    try {
      const roster = await listCompetitionStudents(competitionId);
      setRosterById((prev) => {
        const next = new Map(prev);
        next.set(competitionId, roster);
        return next;
      });
    } finally {
      setRosterLoadingId(null);
    }
  }

  function toggleCase(id: string) {
    if (openCaseId === id) {
      setOpenCaseId(null);
      return;
    }
    setOpenCaseId(id);
    if (!rosterById.has(id)) void loadRoster(id);
  }

  const visible = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter === "active") return r.status === "open" || r.status === "judging";
      if (statusFilter === "closed") {
        return r.status === "closed" || r.status === "final_invoice_pending" || r.status === "final_invoice_paid";
      }
      return true;
    });
  }, [rows, statusFilter]);

  const counts = useMemo(() => {
    const active = rows.filter((r) => r.status === "open" || r.status === "judging").length;
    const closed = rows.length - active;
    return { active, closed };
  }, [rows]);

  return (
    <View style={{ padding: 24, maxWidth: 1080 }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: AMBER }}>
        Campus · Cases
      </Text>
      <Text style={{ marginTop: 6, fontFamily: "CormorantGaramond_300Light", fontSize: 28, lineHeight: 32, color: LINEN }}>
        Cases your students have joined
      </Text>
      <Text style={{ marginTop: 4, fontFamily: "DMSans_300Light", fontSize: 13, color: WM }}>
        {counts.active} active · {counts.closed} closed
      </Text>

      {/* Filter chips */}
      <View style={{ marginTop: 16, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        {(["active", "closed", "all"] as StatusFilter[]).map((s) => (
          <Chip key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} active={statusFilter === s} onPress={() => setStatusFilter(s)} />
        ))}
      </View>

      {/* List */}
      {loading ? (
        <ActivityIndicator style={{ marginTop: 20 }} size="small" color={AMBER} />
      ) : visible.length === 0 ? (
        <View style={{ marginTop: 18, padding: 22, borderRadius: 6, backgroundColor: FOREST_MID, borderWidth: 1, borderColor: BORDER, alignItems: "center" }}>
          <Text style={{ fontFamily: "CormorantGaramond_500Medium", fontSize: 18, color: LINEN }}>
            No cases match this filter.
          </Text>
        </View>
      ) : (
        <View style={{ marginTop: 18, gap: 8 }}>
          {visible.map((row) => {
            const expanded = openCaseId === row.competition_id;
            return (
              <View key={row.competition_id}>
                <Pressable
                  onPress={() => toggleCase(row.competition_id)}
                  style={[
                    {
                      padding: 14,
                      borderRadius: 6,
                      backgroundColor: FOREST_MID,
                      borderWidth: 1,
                      borderColor: expanded ? "rgba(181,135,58,0.32)" : BORDER,
                    } as ViewStyle,
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                    <View style={{ flex: 1, minWidth: 200 }}>
                      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 14, color: LINEN }}>
                        {row.competition_name}
                      </Text>
                      <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11.5, color: WS }}>
                        {row.company_name ?? "—"} · status {row.status}
                      </Text>
                    </View>
                    <Stat label="Participating" value={row.participating_students} />
                    <Stat label="Submitted" value={row.submitted_students} />
                  </View>
                </Pressable>

                {expanded ? (
                  <View
                    style={{
                      marginTop: 8,
                      padding: 12,
                      borderRadius: 6,
                      backgroundColor: "rgba(0,0,0,0.4)",
                      borderWidth: 1,
                      borderColor: BORDER,
                    }}
                  >
                    {rosterLoadingId === row.competition_id ? (
                      <ActivityIndicator size="small" color={AMBER} />
                    ) : (rosterById.get(row.competition_id) ?? []).length === 0 ? (
                      <Text style={{ fontFamily: "DMSans_400Regular", fontSize: 12, color: WM }}>
                        No detailed roster — this RPC scopes to students at your school only.
                      </Text>
                    ) : (
                      <View style={{ gap: 6 }}>
                        {(rosterById.get(row.competition_id) ?? []).map((s) => (
                          <View
                            key={s.student_id}
                            style={{
                              paddingHorizontal: 10,
                              paddingVertical: 8,
                              borderRadius: 4,
                              backgroundColor: FOREST_MID,
                              borderWidth: 1,
                              borderColor: BORDER,
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <View style={{ flex: 1 }}>
                              <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 12, color: LINEN }}>
                                {s.full_name ?? "Student"}
                              </Text>
                              <Text style={{ marginTop: 2, fontFamily: "DMSans_400Regular", fontSize: 11, color: WM }}>
                                {s.major ?? "—"} · {s.graduation_year ?? "—"}
                                {s.team_name ? ` · team ${s.team_name}` : ""}
                              </Text>
                            </View>
                            {s.submitted_count > 0 ? <Pill color={SAGE}>Submitted</Pill> : null}
                            {s.placement != null ? <Pill color={AMBER}>{`Placed ${s.placement}`}</Pill> : null}
                          </View>
                        ))}
                      </View>
                    )}
                    <View style={{ marginTop: 12, flexDirection: "row", gap: 8 }}>
                      <Pressable
                        onPress={() => router.push(`/student/find/competition/${row.competition_id}` as Href)}
                        style={[
                          {
                            paddingHorizontal: 12,
                            paddingVertical: 7,
                            borderRadius: 3,
                            borderWidth: 1,
                            borderColor: BORDER,
                          } as ViewStyle,
                          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                        ]}
                      >
                        <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 10.5, letterSpacing: 1.2, color: WS }}>
                          View case page ↗
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      )}
    </View>
  );
}

function Chip({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? AMBER : BORDER,
          backgroundColor: active ? "rgba(181,135,58,0.12)" : "transparent",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: "DMSans_500Medium", fontSize: 11, color: active ? AMBER : WS }}>{label}</Text>
    </Pressable>
  );
}

function Pill({ children, color }: { children: string; color: string }) {
  return (
    <View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, borderWidth: 1, borderColor: color }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", color }}>
        {children}
      </Text>
    </View>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <View style={{ alignItems: "flex-end" }}>
      <Text style={{ fontFamily: "DMSans_700Bold", fontSize: 9, letterSpacing: 1.2, textTransform: "uppercase", color: WM }}>
        {label}
      </Text>
      <Text style={{ marginTop: 2, fontFamily: "CormorantGaramond_500Medium", fontSize: 18, color: LINEN }}>
        {value}
      </Text>
    </View>
  );
}

// Suppress unused var warnings — WL is provided for inner components that may
// adopt the palette later.
void WL;
